import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Checkbox, CircularProgress, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useKothar from "context/useKothar";
import { usePaginatedSearch } from "hooks/usePaginatedSearch";
import { useState } from "react";
import { BiPlus } from "react-icons/bi";
import { toast } from "react-toastify";
import { addClientToFeedback } from "utils/followUpService";
import { getPaginatedClientData } from "utils/clientService";

const fetchClientPage = ({ page, size, search }) =>
  getPaginatedClientData({ pageIndex: page, pageSize: size, searchText: search });

const FollowupClientSelector = ({
  onClientAdded,
  selectedClients,
  setSelectedClients,
  feedbackClients,
  isFeedbackClientsLoading,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [{ usersList, userDetail }, {}] = useKothar();
  const {
    inputValue: clientSearchInput,
    setInputValue: setClientSearchInput,
    options: clientOptions,
    loading: isClientOptionsLoading,
    listboxProps: clientListboxProps,
  } = usePaginatedSearch(fetchClientPage);
  const [selectedAssignedTo, setSelectedAssignedTo] = useState(null);
  const [priority, setPriority] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [reason, setReason] = useState("");
  const queryClient = useQueryClient();
  const addClientToFeedbackMutation = useMutation({
    mutationFn: addClientToFeedback,
    onSuccess: () => {
      toast.success("Client added successfully");
      queryClient.invalidateQueries({ queryKey: ["feedbackClientsTable"] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.errorMessage || "Error adding client");
    },
  });

  const handleAddClients = async () => {
    if (selectedClients.length === 0) {
      toast.warning("Please select at least one client");
      return;
    }

    setIsAdding(true);
    const clientIds = selectedClients.map((client) => client.id);
    const payload = {
      clientIds: [...clientIds],
      clientFormId: null,
      status: null,
      priority: priority || null,
      assignedToId: selectedAssignedTo?.id ?? null,
      followUpDate: followUpDate || null,
      nextFollowUpDate: followUpDate || null,
      reason: reason?.trim() || null,
      notes: null,
    };
    addClientToFeedbackMutation.mutate(payload);
    setSelectedClients([]);
    setPriority("");
    setFollowUpDate("");
    setReason("");
    setSelectedAssignedTo(null);
    onClientAdded?.();
    setIsAdding(false);
  };
  const filteredClientOptions = clientOptions.filter(
    (client) => !feedbackClients?.some((feedbackClient) => feedbackClient?.client?.id === client.id)
  );

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const orgUsers = usersList.filter((user) => user?.organization?.id === userDetail?.orgId);
  return (
    <>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 items-end">
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-app-text mb-1.5">Assigned To</label>
          <Autocomplete
            options={orgUsers}
            getOptionLabel={(option) => option?.name || `${option?.firstName} ${option?.lastName}`}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name..."
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "background.paper",
                    borderRadius: "10px",
                  },
                }}
              />
            )}
            value={selectedAssignedTo}
            onChange={(event, newValue) => setSelectedAssignedTo(newValue)}
          />
        </div>
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-app-text mb-1.5">Clients</label>
          <Autocomplete
            multiple
            freeSolo
            options={filteredClientOptions}
            filterOptions={(x) => x}
            loading={isClientOptionsLoading}
            inputValue={clientSearchInput}
            onInputChange={(_, value, reason) => {
              if (reason === "reset") return;
              setClientSearchInput(value);
            }}
            disableCloseOnSelect
            value={selectedClients}
            onChange={(event, newValue) => setSelectedClients(newValue)}
            disabled={isAdding}
            limitTags={2}
            getOptionLabel={(option) =>
              `${option.name}${option.passportNumber ? ` · ${option.passportNumber}` : ""}`
            }
            renderOption={(props, option, { selected }) => (
              <li {...props} className="flex gap-2 py-2" key={option.id}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 10 }}
                  checked={selected}
                  size="small"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-app-text truncate">{option.name}</span>
                  <span className="text-xs text-app-text-muted">
                    {option.email || "—"}{" "}
                    {option.passportNumber ? `· ${option.passportNumber}` : ""}
                  </span>
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by name or passport..."
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "background.paper",
                    borderRadius: "10px",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isClientOptionsLoading ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            ListboxProps={clientListboxProps}
          />
        </div>
        <button
          onClick={handleAddClients}
          disabled={isAdding || selectedClients.length === 0}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap shadow-sm"
        >
          {isAdding ? (
            <>
              <CircularProgress size={18} style={{ color: "white" }} />
              Adding...
            </>
          ) : (
            <>
              <BiPlus size={18} />
              Add to queue
              {selectedClients.length > 0 && (
                <span className="ml-0.5 bg-app-surface/20 px-1.5 py-0.5 rounded text-xs font-semibold">
                  {selectedClients.length}
                </span>
              )}
            </>
          )}
        </button>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-5 items-end mt-5">
        <div className="min-w-[140px]">
          <label className="block text-sm font-medium text-app-text mb-1.5">Priority</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="w-full px-3 py-2 border border-app-border rounded-[10px] bg-app-surface text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">Select</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-sm font-medium text-app-text mb-1.5">Follow-up date</label>
          <input
            type="date"
            value={followUpDate}
            onChange={(e) => setFollowUpDate(e.target.value)}
            className="w-full px-3 py-2 border border-app-border rounded-[10px] bg-app-surface text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-app-text mb-1.5">Reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Visa follow-up"
            className="w-full px-3 py-2 border border-app-border rounded-[10px] bg-app-surface text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 placeholder:text-app-text-muted"
          />
        </div>
      </div>
      {selectedClients.length > 0 && (
        <p className="text-xs text-app-text-muted ml-[250px] mt-2">
          {selectedClients.length} client{selectedClients.length !== 1 ? "s" : ""} selected
        </p>
      )}
    </>
  );
};

export default FollowupClientSelector;
